// The two-character codes that make up a tour request code.
//
// A request code such as `X68E-UKAMS6` is assembled from segments, each naming
// one record: `UK` is the Uchikake experience, and a partner has a code of its
// own. The rule is shared here rather than restated per record type, because a
// segment of the wrong length or the wrong alphabet does not just look odd — it
// puts every segment after it where nothing expects to find it.
//
// Uniqueness is a separate question, and a per-collection one: see
// `isExperienceCodeTaken` and `partnerCodesRepo`.

/** Exactly two characters — one segment of a request code. */
export const shortCodeLength = 2;

/**
 * A typed code as it is stored: upper-cased, with whitespace removed, and with
 * `O` and `I` folded onto `0` and `1`.
 *
 * Every writer goes through this, so the stored value, the value a uniqueness
 * check is run against, and the characters that end up in a request code are
 * always the same string.
 *
 * The folding is what makes that last clause true. A request code is spelled in
 * an alphabet that has no `O` and no `I` — they are not worth confusing with
 * `0` and `1` on a code read off one screen and typed into another — so a
 * segment containing either could not be written into one. Folding here rather
 * than at the point of use is deliberate: it means `UO` and `U0` are one code
 * rather than two, so the uniqueness check above cannot hand out both and leave
 * a request code naming either.
 */
export function normaliseShortCode(code: string): string {
  return code.replace(/\s+/g, '').toUpperCase().replace(/O/g, '0').replace(/I/g, '1');
}

/**
 * Why a code cannot be used, or null when it can — shape only; whether another
 * record already holds it is a Firestore question, asked separately.
 *
 * An empty code is allowed. The field is new, so most of both catalogues have
 * none yet, and refusing to save any other field on those records until somebody
 * invents one would be worse than a blank.
 *
 * Letters and digits only, since a request code is split on its hyphen.
 */
export function validateShortCode(code: string): string | null {
  const normalised = normaliseShortCode(code);
  if (!normalised) return null;
  if (!/^[A-Z0-9]+$/.test(normalised)) return 'Use letters and numbers only.';
  if (normalised.length !== shortCodeLength) {
    return `A code is exactly ${shortCodeLength} characters.`;
  }
  return null;
}
